/**
 * Heading collection for the editor preview.
 *
 * MIRRORS PHP: `Blocks/TableOfContents.php` does the authoritative walk at
 * render time. This copy exists so the editor can show a live outline as the
 * author types; where the two could disagree (slugging an exotic character),
 * the frontend wins and this is only a preview. Two runtimes, no shared
 * module — same posture as the Social Icons service registry.
 */

import { getBlockType } from '@wordpress/blocks';

/**
 * Approximate WordPress' sanitize_title() for preview purposes.
 *
 * @param {string} text Heading text.
 * @return {string} Slug.
 */
export function slugify(text) {
  return String(text || '')
    .normalize('NFD')
    // Strip combining marks so "Café" and "Cafe" slug alike, as sanitize_title
    // does through remove_accents().
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/<[^>]*>/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Whether a block's subtree is something other than this page's own outline.
 *
 * MIRRORS PHP: `TableOfContents::is_outside_page_outline()`. In the Site Editor
 * getBlocks() returns the whole template, so without this the preview would
 * list the site header and footer headings — and every product in a loop — that
 * the front end correctly leaves out.
 *
 * @param {string} name Block name.
 * @return {boolean} True when the subtree should be skipped.
 */
function isOutsideOutline(name) {
  if (name === 'core/template-part' || name.endsWith('-no-results')) {
    return true;
  }

  const context = getBlockType(name)?.providesContext;
  return !!context && ('postId' in context || 'queryId' in context);
}

/**
 * Plain text of a heading block's rich-text content.
 *
 * @param {string} html Raw attribute value.
 * @return {string} Text.
 */
function toPlainText(html) {
  const el = document.createElement('div');
  el.innerHTML = String(html || '');
  return (el.textContent || '').trim();
}

/**
 * Walk the editor's block tree and collect headings in the given level range.
 *
 * Headings outside the range still consume a slug, matching the PHP walk — the
 * anchor a heading gets must not depend on the TOC's level settings.
 *
 * @param {Array}  blocks   Editor blocks.
 * @param {number} minLevel Lowest level to include.
 * @param {number} maxLevel Highest level to include.
 * @return {Array} List of { level, text, anchor }.
 */
export function collectHeadings(blocks, minLevel, maxLevel) {
  const counts = {};
  const collected = [];

  const nextSlug = (base) => {
    if (!base) {
      return '';
    }
    if (counts[base]) {
      counts[base] += 1;
      return `${base}-${counts[base]}`;
    }
    counts[base] = 1;
    return base;
  };

  const walk = (list) => {
    (list || []).forEach((block) => {
      if (block.name === 'brandy/table-of-contents' || isOutsideOutline(block.name)) {
        return;
      }

      if (block.name === 'core/heading') {
        const text = toPlainText(block.attributes?.content);
        if (text) {
          const level = Math.min(6, Math.max(1, block.attributes?.level || 2));
          const anchor = block.attributes?.anchor
            ? block.attributes.anchor
            : nextSlug(slugify(text));

          if (anchor && level >= minLevel && level <= maxLevel) {
            collected.push({ level, text, anchor });
          }
        }
      }

      if (block.innerBlocks?.length) {
        walk(block.innerBlocks);
      }
    });
  };

  walk(blocks);
  return collected;
}

/**
 * Nest a flat heading list by level.
 *
 * MIRRORS PHP: `TableOfContents::build_nested_list()`. A deeper heading becomes
 * a child of the item still open above it, so a skipped level (h2 straight to
 * h4) nests exactly one level rather than two, and climbing back out stops at
 * the first shallower ancestor. The editor needs this so its preview is the
 * same shape as the rendered page — it used to draw one flat list with padding
 * standing in for the nesting, which meant list markers and numbering did not
 * match what visitors saw.
 *
 * @param {Array} headings Flat list from collectHeadings().
 * @return {Array} Tree of { level, text, anchor, children }.
 */
export function buildHeadingTree(headings) {
  const root = [];
  const stack = [{ level: -Infinity, children: root }];

  headings.forEach((heading) => {
    const node = { ...heading, children: [] };

    while (stack.length > 1 && stack[stack.length - 1].level >= node.level) {
      stack.pop();
    }

    stack[stack.length - 1].children.push(node);
    stack.push(node);
  });

  return root;
}
